import React, { useRef, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import { Physics, useSphere } from "@react-three/cannon";
import * as THREE from "three";

const BALL_RADIUS = 0.14;
const BALL_COUNT = 20;
const CAGE_RADIUS = 1.2; // slightly smaller than visual sphere (1.4)
const CAGE_CENTER_Y = 0.3;
const CAGE_ANGULAR_SPEED = 2.5; // rad/s around X axis
const WALL_FRICTION = 0.4; // how much cage rotation transfers to balls
const DRAW_ANIMATION_DURATION = 2.8; // seconds for ball to zoom to camera

const Ball = ({ position, frozen = false }) => {
  const [ref, api] = useSphere(() => ({
    mass: 1,
    args: [BALL_RADIUS],
    position,
    restitution: 0.6,
    friction: 0.3,
    linearDamping: 0.1,
    angularDamping: 0.3,
    allowSleep: false,
  }));

  // Freeze ball when drawing starts
  const wasFrozen = useRef(false);
  useEffect(() => {
    if (frozen && !wasFrozen.current) {
      wasFrozen.current = true;
      api.velocity.set(0, 0, 0);
      api.angularVelocity.set(0, 0, 0);
      api.mass.set(0); // Make static
    }
  }, [frozen, api]);

  // Track position and velocity via subscriptions (synced with physics worker)
  const positionRef = useRef([...position]);
  const velocityRef = useRef([0, 0, 0]);

  useEffect(() => {
    const unsubPos = api.position.subscribe((p) => {
      positionRef.current = p;
    });
    const unsubVel = api.velocity.subscribe((v) => {
      velocityRef.current = v;
    });
    return () => {
      unsubPos();
      unsubVel();
    };
  }, [api]);

  useFrame(() => {
    // Use subscribed position (synced with physics)
    const [px, py, pz] = positionRef.current;

    // Ball position relative to cage center
    const x = px;
    const y = py - CAGE_CENTER_Y;
    const z = pz;

    const dist = Math.sqrt(x * x + y * y + z * z);
    const maxDist = CAGE_RADIUS - BALL_RADIUS;

    // Check if ball is outside the spherical cage boundary
    if (dist > maxDist && dist > 0.001) {
      // Normal pointing outward from center
      const nx = x / dist;
      const ny = y / dist;
      const nz = z / dist;

      // Push ball back inside (always)
      api.position.set(
        nx * maxDist,
        ny * maxDist + CAGE_CENTER_Y,
        nz * maxDist
      );

      // Current velocity
      const [vx, vy, vz] = velocityRef.current;

      // Velocity component along the normal (outward)
      const vDotN = vx * nx + vy * ny + vz * nz;

      // Cage surface velocity at this point (rotation around X axis):
      // ω × r = (ω, 0, 0) × (x, y, z) = (0, -ω*z, ω*y)
      const omega = CAGE_ANGULAR_SPEED;
      const surfaceVy = -omega * z;
      const surfaceVz = omega * y;

      // Reflect velocity if moving outward, otherwise just apply friction
      const restitution = 0.5;
      let newVx = vx;
      let newVy = vy;
      let newVz = vz;

      if (vDotN > 0) {
        // Ball is moving outward - reflect it
        newVx = vx - (1 + restitution) * vDotN * nx;
        newVy = vy - (1 + restitution) * vDotN * ny;
        newVz = vz - (1 + restitution) * vDotN * nz;
      }

      // Always apply friction coupling to spinning cage surface
      newVy += WALL_FRICTION * (surfaceVy - newVy);
      newVz += WALL_FRICTION * (surfaceVz - newVz);

      api.velocity.set(newVx, newVy, newVz);
    }
  });

  return (
    <mesh ref={ref} castShadow receiveShadow>
      <sphereGeometry args={[BALL_RADIUS, 16, 16]} />
      <meshStandardMaterial color="#f5f5f5" />
    </mesh>
  );
};

const Balls = ({ frozen = false }) => {
  const positionsRef = useRef(null);

  if (!positionsRef.current) {
    const positions = [];

    for (let i = 0; i < BALL_COUNT; i += 1) {
      // Spread balls around inside the cage
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.6 + 0.2; // avoid poles
      const r = CAGE_RADIUS * (0.3 + Math.random() * 0.5);

      positions.push([
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.cos(phi) + CAGE_CENTER_Y,
        r * Math.sin(phi) * Math.sin(theta),
      ]);
    }

    positionsRef.current = positions;
  }

  return (
    <group>
      {positionsRef.current.map((position, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Ball key={index} position={position} frozen={frozen} />
      ))}
    </group>
  );
};

const Cage = ({ spinning = true }) => {
  const cageRef = useRef();

  useFrame((_, delta) => {
    if (cageRef.current && spinning) {
      // Spin around the horizontal axis, like a real bingo cage
      cageRef.current.rotation.x += delta * CAGE_ANGULAR_SPEED;
    }
  });

  return (
    <group ref={cageRef} position={[0, 0.3, 0]} rotation={[0, 0, 0]}>
      {/* Outer wireframe sphere - thin lines */}
      <mesh>
        <sphereGeometry args={[1.4, 24, 24]} />
        <meshBasicMaterial color="#333" wireframe wireframeLinewidth={0.5} opacity={0.25} transparent />
      </mesh>

      {/* Front and back circular rims */}
      <mesh rotation={[0, 0, 0]}>
        <torusGeometry args={[1.4, 0.03, 8, 64]} />
        <meshStandardMaterial color="#111" />
      </mesh>
      <mesh rotation={[0, 0, 0]} position={[0, 0, -0.05]}>
        <torusGeometry args={[1.4, 0.03, 8, 64]} />
        <meshStandardMaterial color="#111" />
      </mesh>

      {/* Horizontal band */}
      <mesh rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[1.4, 0.02, 8, 64]} />
        <meshStandardMaterial color="#111" />
      </mesh>

      {/* Axle through the sphere */}
      <mesh rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 3.0, 16]} />
        <meshStandardMaterial color="#111" />
      </mesh>
    </group>
  );
};

// The drawn ball that animates out of the cage and zooms to camera
const DrawnBall = ({ number, isAnimating, onComplete }) => {
  const groupRef = useRef();
  const progressRef = useRef(0);
  const hasCompleted = useRef(false);
  const hasStarted = useRef(false);
  
  // Track when animation starts
  if (isAnimating && !hasStarted.current) {
    hasStarted.current = true;
  }
  
  // Starting position (inside cage, slightly up)
  const startPos = useRef(new THREE.Vector3(0, 0.5, 0));
  // Exit position (just outside cage, moving toward camera)
  const exitPos = useRef(new THREE.Vector3(0, 1.5, 2.0));
  // Final position: camera is at z=6, y=1.4, ball radius 0.2 * 5 = 1.0
  // Ball front face at z=4.0+1.0=5.0, safely 1 unit in front of camera
  // Y lower than camera to center the ball in the viewport
  const finalPos = useRef(new THREE.Vector3(0, 0.8, 3.0));
  const FINAL_SCALE = 5;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    
    // Once animation completed, lock the final transform and stop updating
    if (hasCompleted.current) {
      groupRef.current.position.copy(finalPos.current);
      groupRef.current.scale.setScalar(FINAL_SCALE);
      return;
    }
    
    if (!isAnimating) return;
    
    progressRef.current += delta / DRAW_ANIMATION_DURATION;
    const t = Math.min(progressRef.current, 1);
    
    if (t < 0.25) {
      // Phase 1: Rise out of cage (0 to 0.25)
      const phase1T = t / 0.25;
      const eased = 1 - Math.pow(1 - phase1T, 2);
      groupRef.current.position.lerpVectors(startPos.current, exitPos.current, eased);
      // Small scale increase during exit
      groupRef.current.scale.setScalar(1 + eased * 0.3);
    } else {
      // Phase 2: Move toward camera and scale up (0.25 to 1.0)
      const phase2T = (t - 0.25) / 0.75;
      const eased = 1 - Math.pow(1 - phase2T, 3);
      
      groupRef.current.position.lerpVectors(exitPos.current, finalPos.current, eased);
      
      // Scale from 1.3 to FINAL_SCALE (5x)
      // At 5x scale, ball radius = 0.2 * 5 = 1.0
      // At final z=4.0, ball front is at z=5.0, camera at z=6 - nice and visible
      const scale = 1.3 + eased * (FINAL_SCALE - 1.3);
      groupRef.current.scale.setScalar(scale);
    }
    
    // Call onComplete when animation finishes
    if (t >= 1 && !hasCompleted.current) {
      hasCompleted.current = true;
      if (onComplete) onComplete();
    }
  });

  // Only render once animation has started, and keep visible after it completes
  if (!hasStarted.current) return null;

  return (
    <group ref={groupRef} position={[0, 0.5, 0]}>
      {/* Ball sphere */}
      <mesh>
        <sphereGeometry args={[0.2, 32, 32]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Colored band on ball (like real bingo balls) */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.201, 0.201, 0.12, 32]} />
        <meshStandardMaterial color="#259646" />
      </mesh>
      {/* Number on front of ball - larger, on the band */}
      <Text
        position={[0, 0, 0.202]}
        fontSize={0.14}
        color="#000000"
        fontWeight="bold"
        anchorX="center"
        anchorY="middle"
      >
        {number}
      </Text>
      {/* Number on back of ball */}
      <Text
        position={[0, 0, -0.202]}
        rotation={[0, Math.PI, 0]}
        fontSize={0.14}
        color="#000000"
        fontWeight="bold"
        anchorX="center"
        anchorY="middle"
      >
        {number}
      </Text>
    </group>
  );
};

// Scene component that manages the draw animation
const Scene = ({ drawnNumber, isDrawing, onDrawComplete, disableControls }) => {
  return (
    <>
      <color attach="background" args={["#f5f5f5"]} />
      <ambientLight intensity={0.5} />
      <directionalLight position={[4, 6, 4]} intensity={0.9} />
      <directionalLight position={[-4, 2, -2]} intensity={0.3} />
      <Physics gravity={[0, -9.81, 0]}>
        <Balls frozen={isDrawing} />
      </Physics>
      <Cage spinning={!isDrawing} />
      {drawnNumber && (
        <DrawnBall 
          number={drawnNumber} 
          isAnimating={isDrawing} 
          onComplete={onDrawComplete} 
        />
      )}
      {!disableControls && (
        <OrbitControls enableZoom={false} enablePan={false} maxPolarAngle={Math.PI / 2.1} />
      )}
    </>
  );
};

const BingoGlobe = ({ 
  drawnNumber, 
  isDrawing = false, 
  onDrawComplete, 
  fullScreen = false 
}) => {
  return (
    <div style={{ width: "100%", height: fullScreen ? "100%" : "320px" }}>
      <Canvas camera={{ position: [0, 1.4, 6], fov: 40 }}>
        <Scene 
          drawnNumber={drawnNumber}
          isDrawing={isDrawing}
          onDrawComplete={onDrawComplete}
          disableControls={isDrawing}
        />
      </Canvas>
    </div>
  );
};

export default BingoGlobe;
